import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ChildLogin from './pages/auth/ChildLogin';
import ParentLogin from './pages/auth/ParentLogin';
import ParentTwoFactor from './pages/auth/ParentTwoFactor';
import ParentRegister from './pages/auth/ParentRegister';
import EmailVerification from './pages/auth/EmailVerification';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import TwoFactorSetup from './pages/auth/TwoFactorSetup';
import SubjectSelector from './pages/child/SubjectSelector';
import ChildIntro from './pages/child/ChildIntro';
import Chat from './pages/child/Chat';
import SessionHistory from './pages/child/SessionHistory';
import Dashboard from './pages/parent/Dashboard';
import ChildSettings from './pages/parent/ChildSettings';
import ChildSessions from './pages/parent/ChildSessions';
import SessionDetail from './pages/parent/SessionDetail';
import SafeguardingAlerts from './pages/parent/SafeguardingAlerts';
import OnboardingWizard from './pages/parent/OnboardingWizard';
import DataControls from './pages/parent/DataControls';

// Family code values look like UUIDs but we accept any opaque token of
// reasonable length. The regex blocks anything weird ending up in start_url
// (path traversal, control chars) before encodeURIComponent has to deal
// with it.
const FAMILY_ID_PATTERN = /^[A-Za-z0-9-]{1,64}$/;

// When the child arrives via a family link (?familyId=...), point the
// manifest <link> at the server's dynamic endpoint so iOS reads a manifest
// whose start_url already contains the familyId. The server also derives
// familyId from the page Referer for the very first manifest fetch (which
// can fire before this effect runs), so this href update is a belt-and-
// braces second pass — useful for browsers that strip the Referer header.
function applyDynamicManifestForFamilyId() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const familyId = params.get('familyId');
  if (!familyId || !FAMILY_ID_PATTERN.test(familyId)) return;

  const link = document.querySelector('link[rel="manifest"]');
  if (!link) return;

  const newHref = `/manifest.json?familyId=${encodeURIComponent(familyId)}`;
  if (link.getAttribute('href') !== newHref) link.setAttribute('href', newHref);
}

// Catch-all redirect. If the device has visited via a family link — either
// right now (URL still carries familyId) or in a prior session (familyId is
// in localStorage) — it's a child device and should land on /login. Bare
// visits go to the parent login.
//
// CRITICAL: preserve the query string. After the dynamic-manifest fix the
// PWA launches at /?familyId=xxx; if we drop the param the redirect lands
// on /login with nothing to pre-fill, which is exactly the bug we just
// fixed for iOS.
function CatchAllRedirect() {
  const { search } = useLocation();
  let target = '/parent/login';
  const params = new URLSearchParams(search);
  if (params.get('familyId')) {
    target = '/login';
  } else {
    try {
      if (localStorage.getItem('lantern.familyId')) target = '/login';
    } catch { /* storage blocked — fall through to parent login */ }
  }
  return <Navigate to={`${target}${search}`} replace />;
}

function App() {
  useEffect(() => { applyDynamicManifestForFamilyId(); }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* Auth routes */}
        <Route path="/login" element={<ChildLogin />} />
        <Route path="/parent/login" element={<ParentLogin />} />
        <Route path="/parent/2fa" element={<ParentTwoFactor />} />
        <Route path="/parent/2fa/setup" element={<TwoFactorSetup />} />
        <Route path="/parent/register" element={<ParentRegister />} />
        <Route path="/parent/verify-email" element={<EmailVerification />} />
        <Route path="/parent/forgot-password" element={<ForgotPassword />} />
        <Route path="/parent/reset-password" element={<ResetPassword />} />

        {/* Child routes */}
        <Route path="/child" element={<SubjectSelector />} />
        <Route path="/child/subjects" element={<SubjectSelector />} />
        <Route path="/child/intro" element={<ChildIntro />} />
        <Route path="/child/chat/:sessionId" element={<Chat />} />
        <Route path="/child/history" element={<SessionHistory />} />

        {/* Parent routes */}
        <Route path="/parent/dashboard" element={<Dashboard />} />
        <Route path="/parent/onboarding" element={<OnboardingWizard />} />
        <Route path="/parent/sessions/:childId" element={<ChildSessions />} />
        <Route path="/parent/children/:childId/settings" element={<ChildSettings />} />
        <Route path="/parent/session/:sessionId" element={<SessionDetail />} />
        <Route path="/parent/safeguarding" element={<SafeguardingAlerts />} />
        <Route path="/parent/data-controls" element={<DataControls />} />

        <Route path="*" element={<CatchAllRedirect />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
